import React, { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
};

const formatDate = (date, isRTL) =>
  date.toLocaleDateString(isRTL ? 'ar-DZ-u-nu-latn' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const today = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

const trimesterOf = (week, t) => {
  if (week <= 13) return t('pregnancy_trimester_1');
  if (week <= 27) return t('pregnancy_trimester_2');
  return t('pregnancy_trimester_3');
};

const BABY_SIZE_BY_WEEK = [
  [4, 'un grain de pavot', 'بذرة خشخاش'], [6, 'un grain de riz', 'حبة أرز'], [8, 'une framboise', 'حبة توت العليق'], [10, 'une prune', 'حبة برقوق'],
  [12, 'une figue', 'حبة تين'], [14, 'un citron', 'حبة ليمون'], [16, 'un avocat', 'حبة أفوكادو'], [18, 'une patate douce', 'بطاطا حلوة'],
  [20, 'une banane', 'موزة'], [22, 'une papaye', 'حبة بابايا'], [24, 'un épi de maïs', 'كوز ذرة'], [26, 'une laitue', 'رأس خس'],
  [28, 'une aubergine', 'حبة باذنجان'], [30, 'un chou', 'رأس ملفوف'], [32, 'une noix de coco', 'جوزة هند'], [34, 'un melon', 'حبة شمام'],
  [36, 'une laitue romaine', 'خس روماني'], [38, 'un poireau', 'كرّاث'], [40, 'une petite pastèque', 'بطيخة صغيرة'],
];

const babySizeFor = (week, isRTL) => {
  let match = BABY_SIZE_BY_WEEK[0][isRTL ? 2 : 1];
  for (const [w, labelFr, labelAr] of BABY_SIZE_BY_WEEK) {
    if (week >= w) match = isRTL ? labelAr : labelFr;
  }
  return match;
};

const PregnancyTracker = () => {
  const { t, isRTL } = useLanguage();
  const [lastPeriod, setLastPeriod] = useState('');
  const [copied, setCopied] = useState(false);

  const lmp = parseDate(lastPeriod);
  const dueDate = lmp ? addDays(lmp, 280) : null;
  const daysPregnant = lmp ? Math.floor((today() - lmp) / 86400000) : null;
  const currentWeek = daysPregnant != null ? Math.floor(daysPregnant / 7) : null;
  const currentDay = daysPregnant != null ? daysPregnant % 7 : null;
  const daysRemaining = dueDate ? Math.max(0, Math.floor((dueDate - today()) / 86400000)) : null;
  const valid = currentWeek != null && currentWeek >= 0 && currentWeek <= 45;

  const shareText = valid
    ? `👶 ${t('pregnancy_share_pregnancy_label')} ${currentWeek} ${t('pregnancy_week_unit')} ${t('pregnancy_and')} ${currentDay} ${t('pregnancy_day_unit')} (${trimesterOf(currentWeek, t)})\n` +
      `📏 ${t('pregnancy_share_baby_size_label')} ${babySizeFor(currentWeek, isRTL)}\n` +
      `📅 ${t('pregnancy_due_date_label')} : ${formatDate(dueDate, isRTL)}\n` +
      `⏳ ${daysRemaining} ${t('pregnancy_days_word')}\n` +
      `${t('pregnancy_share_computed_with')}`
    : '';

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard indisponible, on ignore silencieusement
    }
  };

  return (
    <ToolPageLayout
      title={t('pregnancy_title')}
      subtitle={t('pregnancy_subtitle')}
      specialty="gynecologue"
    >
      <div>
        <Label className="mb-1 block">{t('ovulation_lmp_label')}</Label>
        <Input
          type="date"
          value={lastPeriod}
          onChange={(e) => setLastPeriod(e.target.value)}
          className="rounded-xl max-w-xs"
        />
      </div>

      {lmp && !valid && (
        <p className="mt-6 text-sm text-[#EF4444]">{t('pregnancy_invalid_date')}</p>
      )}

      {valid && (
        <div className="mt-8 space-y-4">
          <div className="bg-[#E6F4FB] rounded-2xl p-6 border border-[#0596DE]/20 text-center">
            <div className="text-sm text-[#64748B] mb-1">{t('pregnancy_you_are_at')}</div>
            <div className="text-3xl font-bold text-[#0F172A]">
              {currentWeek} {t('pregnancy_week_unit')} {t('pregnancy_and')} {currentDay} {t('pregnancy_day_unit')}
            </div>
            <div className="mt-1 text-[#0596DE] font-semibold">{trimesterOf(currentWeek, t)}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-[#E2E8F0]">
              <div className="text-sm text-[#64748B] mb-1">{t('pregnancy_due_date_label')}</div>
              <div className="text-lg font-semibold text-[#0F172A]">{formatDate(dueDate, isRTL)}</div>
            </div>
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-[#E2E8F0]">
              <div className="text-sm text-[#64748B] mb-1">{t('pregnancy_days_remaining_label')}</div>
              <div className="text-lg font-semibold text-[#0F172A]">{daysRemaining} {t('pregnancy_days_word')}</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#0596DE] to-[#047CBA] rounded-2xl p-6 text-white print:hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">{t('pregnancy_card_title')}</h3>
              <Share2 className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-white/90 text-sm mb-1">
              👶 {currentWeek} {t('pregnancy_week_unit')} {t('pregnancy_and')} {currentDay} {t('pregnancy_day_unit')} — {trimesterOf(currentWeek, t)}
            </p>
            <p className="text-white/90 text-sm mb-1">📏 {t('pregnancy_card_size_prefix')} {babySizeFor(currentWeek, isRTL)}</p>
            <p className="text-white/90 text-sm mb-4">📅 {t('pregnancy_card_due_prefix')} {formatDate(dueDate, isRTL)}</p>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 bg-white text-[#0596DE] font-medium text-sm px-4 py-2 rounded-xl hover:bg-white/90 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? t('pregnancy_copied') : t('pregnancy_copy_button')}
            </button>
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('pregnancy_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default PregnancyTracker;
